import _ from 'lodashParts'
import {init} from 'wixckloader'
import constants from '../utils/constants'

init()

class CkBridge {
    constructor(editor) {

        this.listeners = new WeakMap()

        this.getCmd = cmdName => editor.getCommand(cmdName)

        this.hasSelection = editor.WIX.hasSelection
        this.isMultipleBlockSelection = editor.WIX.isMultipleBlockSelection

        this.getData = () => editor.getData()

        this.fire = cmdName => editor.fire(cmdName)

        this.resetUndo = () => editor.resetUndo()

        this.undo = () => editor.commands.undo.exec()
        this.canUndo = () => editor.undoManager.hasUndo

        this.redo = () => editor.commands.redo.exec()
        this.canRedo = () => editor.undoManager.hasRedo

        this.resize = (width, height) => {
            editor.resize(width || '100%', height, true, false, false)
        }

        this.getDimensions = () => ({
            height: editor.ui.space('contents').$.clientHeight,
            width: editor.ui.space('contents').$.clientWidth
        })

        this.scrollToTop = () => {
            const doc = editor.editable().getDocument().$
            const body = doc && doc.querySelector('body')

            if (body) {
                body.scrollTop = 0
            }
        }

        this.focus = () => {
            const sel = editor.getSelection()
            sel.ignoreFillingCharRemoveOnSelectionChange()
            editor.focus()
            sel.resumeFillingCharRemoveOnSelectionChange()
        }

        this.collapseSelection = () => {
            const selection = editor.getSelection()
            const range = selection && selection.getRanges()[0]
            if (range) {
                range.collapse()
                range.select()
            }
        }

        this.setKeystroke = (keyId, event) => {
            editor.setKeystroke(keyId, event)
        }

        this.setReadOnly = (isReadOnly = true) => {
            const body = editor.document.$.body
            body.setAttribute('contenteditable', !isReadOnly)
        }

        this.getCmdState = cmdName => {
            var cmd = editor.getCommand(cmdName)

            if (cmd) {
                let canPerformCmd = true
                if (cmd.canPerformCmd) {
                    canPerformCmd = cmd.canPerformCmd(editor)
                    if (canPerformCmd) {
                        cmd.refresh(editor)
                    }
                }

                return canPerformCmd ? cmd.state : constants.TRISTATE_DISABLED
            }

            return ''
        }

        this.on = (eventName, callback) => {
            let listener = null
            if (eventName === 'copy' || eventName === 'cut') {
                listener = editor.editable().on(eventName, callback)
            } else if (eventName.indexOf('.') > -1) {
                const cmdName = eventName.split('.')[0]
                const event = eventName.split('.')[1]

                const command = editor.getCommand(cmdName)

                if (command && command.on) {
                    listener = command.on(event, callback)
                }
            } else {
                listener = editor.on(eventName, callback)
            }

            this.listeners.set(callback, listener)
            return callback
        }

        this.removeListener = listener => {
            if (this.listeners.has(listener)) {
                this.listeners.get(listener).removeListener()
                this.listeners.delete(listener)
            }
        }

        this.destroy = () => editor.destroy()

        this.once = (eventName, callback) => editor.once(eventName, callback)

        this.execCmd = (cmdName, params, {execWithoutFocus, execWithoutHistory} = {}) => {
            const editorFocus = editor.getCommand(cmdName).editorFocus
            const canUndo = editor.getCommand(cmdName).canUndo
            if (execWithoutFocus) {
                editor.getCommand(cmdName).editorFocus = false
            }

            if (execWithoutHistory) {
                editor.getCommand(cmdName).canUndo = false
            }

            editor.execCommand(cmdName, params)
            editor.getCommand(cmdName).editorFocus = editorFocus
            editor.getCommand(cmdName).canUndo = canUndo
        }

        this.setData = (data, {setSelectionOnStart, validateHtml = true, cursorLocation = constants.CURSOR_LOCATIONS.START, callback = _.noop} = {}) => {
            cursorLocation = setSelectionOnStart ? cursorLocation : constants.CURSOR_LOCATIONS.NONE

            if (cursorLocation === constants.CURSOR_LOCATIONS.NONE) {
                editor.WIX.setData(data, {validateHtml, callback})
            } else {
                editor.WIX.setDataWithSelectionAt(data, {validateHtml, cursorLocation, callback})
            }
        }

        this.showEditor = () => {
            editor.container.show()
        }

        this.getEditorDOMElement = () => editor && editor.document && editor.document.$

        this.isValidHTML = editor.WIX.isValidHTML

        if (window.isRunningTests) {
            this.setDataWithSelection = htmlData => editor.WIX.TestUtils.setDataWithSelection(editor, htmlData)
        }
    }
}

const _createInstance = (getDomRef, cb, cfg) => {
    if (isCKLoaded()) {
        const editor = CKEDITOR.replace(getDomRef(), cfg)
        editor.once('instanceReady', evt => {
            const _editor = evt.editor
            cb(_editor, new CkBridge(_editor))
        })
    } else {
        window.setTimeout(() => _createInstance(getDomRef, cb, cfg), 50)
    }
}
function createInstance({domElement, onInstanceReadyCallBack, autoGrow, autoGrowOnStartup, autoGrowMinHeight, autoGrowMaxHeight}) {
    const ckCfg = {
        removePlugins: autoGrow ? '' : 'autogrow',
        autoGrow_onStartup: autoGrowOnStartup,
        autoGrow_minHeight: autoGrowMinHeight,
        autoGrow_maxHeight: autoGrowMaxHeight
    }

    _createInstance(() => domElement, (editor, ckBridge) => {
        onInstanceReadyCallBack(ckBridge) //Need to do this so the toolbar will have the correct state
    }, ckCfg)
}

function createOfflineInstance(onInstanceReadyCallBack) {
    let domNodeRef
    const getDomRef = () => {
        domNodeRef = window.document.createElement('div')
        domNodeRef.style.position = 'absolute'
        domNodeRef.style.position = '-150%'
        window.document.body.appendChild(domNodeRef)
        return domNodeRef
    }
    _createInstance(getDomRef, (editor, ckBridge) => {
        editor.container.show()
        _.forEach(editor.commands, editorCmd => {
            editorCmd.editorFocus = false
            editor.focus = () => {}
        })
        onInstanceReadyCallBack({
            destroy: () => {
                setTimeout(() => {
                    ckBridge.destroy()
                    window.document.body.removeChild(domNodeRef)
                }, 10)
            },
            execOfflineCommands: (data, cmds) => {
                editor.WIX.setHtmlWithSelectAll(data)
                _.forEach(cmds, (params, cmdName) => {
                    if (editor.getCommand(cmdName)) {
                        editor.execCommand(cmdName, params)
                    }
                })
                return editor.editable().getHtml()
            }
        }) //Need to do this so the toolbar will have the correct state
    })
}

function isCKLoaded() {
    return _.get(window, 'CKEDITOR.WIX.status') === 'ready'
}

module.exports = {
    createInstance,
    createOfflineInstance
}



// WEBPACK FOOTER //
// ./src/ckUtils/ckBridge.js